import React, { useMemo } from 'react';
import { microwave } from '@round/api';
import { ColumnDef } from '@tanstack/react-table';
import { Skeleton } from '@round/ui-kit';
import Checkbox from 'ui/DataEntry/Checkbox/Checkbox';
import { formatNumberToKNotation, showNotification } from '../../../../../../../helpers';
import DueDate from 'ui-new/DataDisplay/Date/DueDate/DueDate';
import {
    OutstandingPaymentsSortingKey,
    OutstandingPaymentsTableCellContext,
    OutstandingPaymentsTableRow,
} from '../../helpers';
import WrapperPaginationTable, {
    WrapperPaginationTableProps,
} from 'ui/WrapperTable/WrapperPaginationTable/WrapperPaginationTable';
import styles from '../../OutstandingPaymentsTable.module.css';
import { useCheckUserGroupsAccess } from '../../../../../../Auth/hooks/useCheckUserGroupsAccess';
import useNonNullContext from 'Hooks/useNonNullContext';
import { OptionsContext } from 'contexts/OptionsContext/OptionsContext';
import { mapOrderingToTableSorting, mapTableSortingToOrdering } from 'ui/WrapperTable/helpers';
import AccountCell from '../../../TableCells/AccountCell/AccountCell';
import CostLabel from 'ui/DataDisplay/Money/CostLabel/CostLabel';
import CostCurrencyCell from '../../../TableCells/CostCurrencyCell/CostCurrencyCell';
import ReferenceCell from '../../../TableCells/ReferenceCell/ReferenceCell';
import ReleaseCell from '../../../TableCells/ReleaseCell/ReleaseCell';
import Button from 'ui/Button/Button';
import PlatformCell from '../../../TableCells/PlatformCell/PlatformCell';
import DateCell from '../../../TableCells/DateCell/DateCell';
import TableInput from 'ui-new/TableComponents/TableInput/TableInput';

type Props = Omit<WrapperPaginationTableProps<OutstandingPaymentsTableRow>, 'columns' | 'columnPinning'> & {
    totals: microwave.TiktokMicrowaveInfluencerPostTotals | null;
    ordering: OutstandingPaymentsSortingKey[];
    onOrderingChange: (ordering: OutstandingPaymentsSortingKey[]) => void;
    updatePayment: typeof microwave.patchPaymentRequest;
    onClickMakePayment: (row: OutstandingPaymentsTableRow) => void;
};

const OutstandingPaymentsTable = ({
    totals,
    ordering,
    onOrderingChange,
    updatePayment,
    onClickMakePayment,
    ...props
}: Props) => {
    const isUserMicrowaveFinance = useCheckUserGroupsAccess(['microwave_finance']);
    const { currencies } = useNonNullContext(OptionsContext);

    const columns: ColumnDef<OutstandingPaymentsTableRow, any>[] = useMemo(
        () => [
            {
                id: 'select',
                header: ({ table }) => {
                    return (
                        <div className={styles.checkboxContainer}>
                            {props.data.length > 0 && (
                                <Checkbox
                                    ariaLabel="Select all"
                                    value={table.getIsAllPageRowsSelected()}
                                    indeterminateValue={table.getIsSomePageRowsSelected()}
                                    onChange={table.toggleAllPageRowsSelected}
                                />
                            )}
                        </div>
                    );
                },
                cell: ({ row }) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.checkboxContainer}>
                            <Checkbox
                                ariaLabel="Select row"
                                value={row.getIsSelected()}
                                onChange={() => row.toggleSelected()}
                            />
                        </div>
                    );
                },
            },
            {
                header: 'Account',
                accessorKey: 'influencer',
                cell: ({ row: { original } }: OutstandingPaymentsTableCellContext<'influencer'>) => (
                    <AccountCell
                        influencer={original.influencer || null}
                        influencerAccount={original.influencerAccount || null}
                        imageUrl={original.userImageUrl}
                        postUrl={original.post_url}
                        platform={original.platform}
                        isLoading={props.isLoading}
                    />
                ),
            },
            {
                header: 'Platform',
                accessorKey: 'platform',
                cell: ({ getValue }: OutstandingPaymentsTableCellContext<'platform'>) => (
                    <PlatformCell platform={getValue()} isLoading={props.isLoading} />
                ),
            },
            {
                header: 'Posted at',
                accessorKey: 'posted_at',
                cell: ({ getValue }: OutstandingPaymentsTableCellContext<'posted_at'>) => (
                    <DateCell value={getValue()} isLoading={props.isLoading} />
                ),
            },
            {
                header: 'Due date',
                accessorKey: 'payment_deadline',
                cell: ({ getValue }: OutstandingPaymentsTableCellContext<'payment_deadline'>) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    const date = getValue();
                    if (!date) {
                        return '-';
                    }

                    return <DueDate value={date} />;
                },
            },
            {
                header: 'Release',
                accessorKey: 'release',
                cell: ({ getValue, row }: OutstandingPaymentsTableCellContext<'release'>) => (
                    <ReleaseCell release={getValue()} platform={row.original.platform} isLoading={props.isLoading} />
                ),
            },
            {
                accessorKey: 'cost',
                header: 'Cost',
                cell: function RequestCost({
                    getValue,
                    row: { original },
                }: OutstandingPaymentsTableCellContext<'cost'>) {
                    const cost = getValue();

                    if (cost === null) {
                        return <>-</>;
                    }

                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <CostCurrencyCell
                            cost={cost}
                            currencyId={original.currency_id}
                            updatePayment={({ cost, currency_id }) =>
                                updatePayment(original.id, {
                                    cost,
                                    currency_id,
                                })
                            }
                        />
                    );
                },
                footer: () =>
                    Object.entries(totals?.cost || {}).map(([currencyId, amount]) => (
                        <div key={currencyId} className={styles.total}>
                            <CostLabel
                                cost={amount}
                                currencySymbol={currencies.find((curr) => curr.id === Number(currencyId))?.symbol ?? ''}
                            />
                        </div>
                    )),
            },
            {
                header: 'Post views',
                accessorKey: 'view_count',
                enableSorting: true,
                cell: ({ getValue }: OutstandingPaymentsTableCellContext<'view_count'>) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    const viewCount = getValue();

                    return <>{viewCount ? formatNumberToKNotation(viewCount) : '-'}</>;
                },
            },
            {
                header: 'Reference',
                accessorKey: 'payment_reference',
                cell: ({ getValue, row: { original } }: OutstandingPaymentsTableCellContext<'payment_reference'>) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <ReferenceCell
                            reference={getValue()}
                            updatePayment={(val) => updatePayment(original.id, { payment_reference: val })}
                            isReadonly={!isUserMicrowaveFinance}
                        />
                    );
                },
            },
            {
                header: 'Paypal email',
                accessorKey: 'paypal_email_address',
                cell: ({
                    getValue,
                    row: { original },
                }: OutstandingPaymentsTableCellContext<'paypal_email_address'>) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <TableInput
                            value={getValue() || '-'}
                            updateValue={async (val) => {
                                try {
                                    const response = await updatePayment(original.id, { paypal_email_address: val });

                                    if (response.status !== 200) {
                                        const errorMessage =
                                            response.status === 404
                                                ? response.data.detail
                                                : Array.isArray(response.data.paypal_email_address)
                                                  ? response.data.paypal_email_address.join(', ')
                                                  : response.data.paypal_email_address;
                                        showNotification(
                                            errorMessage || 'Could not update PayPal email address',
                                            'error'
                                        );

                                        return false;
                                    }

                                    showNotification('PayPal email address updated', 'info');
                                    return true;
                                } catch {
                                    showNotification('Could not update PayPal email address', 'error');
                                    return false;
                                }
                            }}
                            isReadonly={!isUserMicrowaveFinance}
                        />
                    );
                },
            },
            {
                header: 'Pay',
                id: 'payment',
                cell: ({ row: { original } }) => {
                    if (props.isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <div className={styles.paymentActions}>
                            <Button appearance="outlined" onClick={() => onClickMakePayment(original)}>
                                Pay
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [props.data, props.isLoading, updatePayment, onClickMakePayment, totals, currencies, isUserMicrowaveFinance]
    );

    return (
        <WrapperPaginationTable
            {...props}
            columns={columns}
            className={styles.table}
            columnPinning={{ right: ['payment'] }}
            columnVisibility={{ payment: !!isUserMicrowaveFinance, select: !!isUserMicrowaveFinance }}
            shouldDisplayFooter
            enableSorting
            manualSorting
            sorting={mapOrderingToTableSorting(ordering)}
            onSortingChange={(sortingState) => onOrderingChange(mapTableSortingToOrdering(sortingState))}
        />
    );
};

export default OutstandingPaymentsTable;
